/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Me · Leaves — mobile-first apply + balance + history
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarOff, Plus, X, Calendar, FileText, Loader2,
} from "lucide-react";

const LEAVE_TYPES = [
  { code: "EL",  label: "Earned Leave",   tone: "bg-emerald-100 text-emerald-800" },
  { code: "PL",  label: "Personal Leave", tone: "bg-amber-100 text-amber-800" },
  { code: "SL",  label: "Sick Leave",     tone: "bg-red-100 text-red-800" },
  { code: "OD",  label: "On Duty",        tone: "bg-indigo-100 text-indigo-800" },
];

function todayIso() { return new Date().toISOString().slice(0, 10); }
function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
  } catch { return d; }
}

export default function MeLeaves() {
  const [balance, setBalance] = useState({});
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([
        api.get("/my-portal/dashboard"),
        api.get("/my-portal/leaves?limit=30"),
      ]);
      setBalance(d.data?.leave_balance || {});
      setList(l.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="me-leaves">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">My Leaves</div>
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Leave Balance &amp; History</h1>
      </div>

      {/* Balance chips */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { code: "EL", label: "Earned", tone: "ring-emerald-200 bg-emerald-50/40 text-emerald-900" },
          { code: "PL", label: "Personal", tone: "ring-amber-200 bg-amber-50/40 text-amber-900" },
          { code: "SL", label: "Sick", tone: "ring-red-200 bg-red-50/40 text-red-900" },
          { code: "CL", label: "Casual", tone: "ring-indigo-200 bg-indigo-50/40 text-indigo-900" },
        ].map((b) => (
          <div key={b.code}
               data-testid={`me-leave-bal-${b.code}`}
               className={`rounded-xl ring-1 p-3 ${b.tone}`}>
            <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{b.label}</div>
            <div className="text-2xl font-bold mt-0.5" style={{ fontFamily: "Chivo" }}>
              {Math.max(0, Number(balance[b.code] ?? 0))}
            </div>
            <div className="text-[10px] opacity-70 mt-0.5">
              Used: {balance[`${b.code}_used_ytd`] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Apply button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="me-leave-apply-btn"
        className="w-full rounded-xl bg-red-600 hover:bg-red-700 text-white py-3 font-bold text-sm flex items-center justify-center gap-2"
      >
        <Plus className="h-4 w-4" /> Apply Leave
      </button>

      {/* History */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 px-1">
          Recent Requests
        </div>
        {loading ? (
          <div className="text-center text-xs text-slate-400 py-8">Loading…</div>
        ) : list.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-8 bg-white rounded-xl ring-1 ring-slate-200">
            No leaves applied yet
          </div>
        ) : (
          <div className="space-y-1.5">
            {list.map((l) => (
              <div key={l.id} className="bg-white rounded-xl ring-1 ring-slate-200 p-3"
                   data-testid={`me-leave-row-${l.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-bold text-slate-900">{l.leave_type}</span>
                      <span className="text-[10px] text-slate-500">{l.days}d</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {fmtDate(l.from_date)} → {fmtDate(l.to_date)}
                    </div>
                  </div>
                  <StatusChip status={l.status} />
                </div>
                {l.reason && (
                  <div className="text-[10px] text-slate-500 mt-1 line-clamp-2">{l.reason}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {open && <ApplyLeaveSheet onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
    cancelled: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${
      map[status] || "bg-slate-100 text-slate-700"
    }`}>{status}</span>
  );
}

function ApplyLeaveSheet({ onClose, onSaved }) {
  const [leaveType, setLeaveType] = useState("EL");
  // EL must be applied 24h in advance — default to tomorrow.
  const [fromDate, setFromDate] = useState(tomorrowIso());
  const [toDate, setToDate] = useState(tomorrowIso());
  const [halfDay, setHalfDay] = useState(false);
  const [halfSession, setHalfSession] = useState("morning");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // When user switches to SL/OD which allow same-day apply, suggest today.
  // When switching to EL/PL, ensure dates are at least tomorrow.
  const onTypeChange = (code) => {
    setLeaveType(code);
    if ((code === "SL" || code === "OD") && fromDate === tomorrowIso()) {
      setFromDate(todayIso());
      setToDate(todayIso());
    } else if ((code === "EL" || code === "PL") && fromDate < tomorrowIso()) {
      setFromDate(tomorrowIso());
      setToDate(tomorrowIso());
    }
  };

  const days = useMemo(() => {
    if (halfDay) return 0.5;
    try {
      const a = new Date(fromDate);
      const b = new Date(toDate);
      if (b < a) return 0;
      return Math.round((b - a) / 86400000) + 1;
    } catch { return 0; }
  }, [fromDate, toDate, halfDay]);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please enter a reason");
    if (!halfDay && new Date(toDate) < new Date(fromDate)) return toast.error("Invalid date range");
    setBusy(true);
    try {
      await api.post("/my-portal/leaves/apply", {
        leave_type: leaveType,
        from_date: fromDate,
        to_date: halfDay ? fromDate : toDate,
        half_day: halfDay,
        half_day_session: halfDay ? halfSession : null,
        reason: reason.trim(),
      });
      toast.success("Leave request submitted ✓");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 max-h-[92vh] overflow-y-auto"
        style={{ touchAction: "pan-y", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold">Apply Leave</h3>
          <button onClick={onClose} data-testid="me-leave-close" className="p-1 text-slate-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Type chips */}
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Type</div>
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {LEAVE_TYPES.map((t) => (
            <button key={t.code}
              onClick={() => onTypeChange(t.code)}
              data-testid={`me-leave-type-${t.code}`}
              className={`py-2 rounded-lg text-xs font-semibold ring-1 transition ${
                leaveType === t.code ? `${t.tone} ring-current` : "bg-white text-slate-700 ring-slate-200"
              }`}>
              <div className="text-[10px] opacity-70">{t.code}</div>
              <div>{t.label}</div>
            </button>
          ))}
        </div>

        {/* Half day toggle */}
        <label className="flex items-center gap-2 text-xs text-slate-700 mb-3">
          <input type="checkbox" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)}
                 data-testid="me-leave-half" className="h-4 w-4" />
          Half day
        </label>

        {halfDay && (
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Session</div>
            <div className="grid grid-cols-2 gap-1.5">
              {["morning", "afternoon"].map((s) => (
                <button key={s} onClick={() => setHalfSession(s)}
                  className={`py-2 rounded-lg text-xs font-semibold ring-1 ${
                    halfSession === s ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-slate-200"
                  }`}>
                  {s === "morning" ? "Morning" : "Afternoon"}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">From</div>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                   data-testid="me-leave-from"
                   className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">To</div>
            <input type="date" value={halfDay ? fromDate : toDate} disabled={halfDay}
                   onChange={(e) => setToDate(e.target.value)} data-testid="me-leave-to"
                   className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400" />
          </div>
        </div>

        {/* Days */}
        <div className="text-[11px] text-slate-600 mb-3 text-center font-semibold">
          Total: <span className="text-base text-slate-900 font-bold">{days}</span> day{days !== 1 ? "s" : ""}
        </div>

        {/* EL/PL 24h advance hint */}
        {(leaveType === "EL" || leaveType === "PL") && fromDate <= todayIso() && (
          <div className="text-[11px] text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-lg p-2 mb-3">
            <strong>Note:</strong> {leaveType === "EL" ? "Earned" : "Personal"} Leave must be applied
            at least 24 hours in advance. Please pick tomorrow or later.
          </div>
        )}

        {/* Reason */}
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Reason</div>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder="Brief reason for leave…"
                    data-testid="me-leave-reason"
                    className="w-full rounded-lg ring-1 ring-slate-300 px-2 py-2 text-sm" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="rounded-xl ring-1 ring-slate-300 py-3 text-sm font-semibold text-slate-700">Cancel</button>
          <button onClick={submit} disabled={busy || !reason.trim()}
                  data-testid="me-leave-submit"
                  className="rounded-xl bg-red-600 text-white py-3 text-sm font-bold disabled:bg-slate-300 flex items-center justify-center gap-1">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? "Submitting…" : "Submit Leave"}
          </button>
        </div>
      </div>
    </div>
  );
}
